import { Check, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { trpc } from "~/api/trpc";
import { Textarea } from "~/components/ui/textarea";
import { Language, useLanguageStore } from "~/stores/language-store";

type MessageSectionProps = {
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    // When true, typing only edits a local draft; onChange fires once "Speichern" is pressed
    commitOnSave?: boolean;
};

const MessageSection = ({ value, onChange, readOnly = false, commitOnSave = false }: MessageSectionProps) => {
    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const [isOpen, setIsOpen] = useState(false);
    const messageBeforeEditRef = useRef(value);
    const [draft, setDraft] = useState(value);

    const editedValue = commitOnSave ? draft : value;
    const setEditedValue = commitOnSave ? setDraft : onChange;

    const label = language === Language.GERMAN ? 'Nachricht hinzufügen (optional)' : 'Add message (optional)';
    const savedMessage = value.trim();

    const handleOpen = () => {
        messageBeforeEditRef.current = value;
        setDraft(value);
        setIsOpen(true);
    };

    const handleCancel = () => {
        if (!commitOnSave) {
            onChange(messageBeforeEditRef.current);
        }
        setIsOpen(false);
    };

    const handleSave = () => {
        onChange(editedValue.trim());
        setIsOpen(false);
    };

    if (readOnly) {
        if (!savedMessage) {
            return null;
        }
        return (
            <div className='flex flex-row items-start gap-3 px-4 py-2.5 rounded-xl border border-burgundy/30 bg-white'>
                <Check className='w-4 h-4 text-burgundy shrink-0 mt-0.5' />
                <span className='flex-1 min-w-0 text-sm font-light text-burgundy whitespace-pre-wrap'>
                    {savedMessage}
                </span>
            </div>
        );
    }

    if (!isOpen && savedMessage) {
        return (
            <div className='flex flex-row items-center gap-3 px-4 py-2.5 rounded-xl border border-burgundy/30 bg-white'>
                <Check className='w-4 h-4 text-burgundy shrink-0' />
                <span className='flex-1 min-w-0 truncate text-sm font-light text-burgundy'>
                    {savedMessage}
                </span>
                <button
                    type="button"
                    onClick={handleOpen}
                    className='shrink-0 text-xs font-light text-burgundy underline underline-offset-2 hover:text-burgundy/70'
                >
                    {language === Language.GERMAN ? 'bearbeiten' : 'edit'}
                </button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col rounded-xl border bg-white transition-colors ${isOpen ? 'border-burgundy' : 'border-burgundy/30'}`}>
            <button
                type="button"
                onClick={isOpen ? handleSave : handleOpen}
                className='flex flex-row items-center gap-3 px-4 py-2.5 text-left'
            >
                <Plus className='w-4 h-4 text-burgundy shrink-0' />
                <span className='flex-1 text-sm font-light text-burgundy'>{label}</span>
                {isOpen
                    ? <ChevronDown className='w-4 h-4 text-burgundy/50 shrink-0' />
                    : <ChevronRight className='w-4 h-4 text-burgundy/50 shrink-0' />
                }
            </button>

            {isOpen && (
                <div className='flex flex-col gap-3 px-4 pb-3'>
                    <Textarea
                        placeholder={language === Language.GERMAN ? (cmsConfig?.messagePlaceholderDe ?? 'Deine Nachricht an uns...') : (cmsConfig?.messagePlaceholderEn ?? 'Your message to us...')}
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        autoFocus
                        className="shadow-none border border-burgundy/40 bg-gray-50 text-burgundy md:text-xs placeholder:text-burgundy/50 placeholder:text-sm md:placeholder:text-xs focus-visible:ring-burgundy/50 transition-colors min-h-[80px] rounded-md"
                        rows={3}
                    />
                    <div className='flex flex-row justify-end gap-2'>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className='px-4 py-2 rounded-full border border-burgundy text-xs font-normal text-burgundy hover:bg-burgundy/5 transition-colors'
                        >
                            {language === Language.GERMAN ? 'Abbrechen' : 'Cancel'}
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className='px-4 py-2 rounded-full border border-burgundy bg-burgundy text-xs font-normal text-white hover:bg-burgundy/90 transition-colors'
                        >
                            {language === Language.GERMAN ? 'Speichern' : 'Save'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageSection;
